import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { add, getDate, getDay, isSunday, isToday, sub } from 'date-fns';
import { Observable, of } from 'rxjs';

interface DataPoint {
  index: number;
  isToday: boolean;
  isInThePast: boolean;
  dayOfMonth: number;
  dayOfWeek: number;
  previousDayOfWeek: DataPoint;
  count: number;
  backgroundClass: string;
  textClass: string;
}

@Component({
  selector: 'lohi-heat-map-calendar',
  templateUrl: './heat-map-calendar.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatMapCalendarComponent implements OnInit {
  public points$: Observable<DataPoint[]>;
  public hoveredPoint: any;

  // Will probably make this a service later but I don't even want to accidentally make the call for non-internal users
  constructor(private http: HttpClient, private cd: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.loadData();
  }

  public setHoveredPoint(point: any) {
    this.hoveredPoint = point;
  }

  private async loadData() {
    const data = await this.http
      .get<{ futureDriverCounts: number[] }>(`${environment.api}/v1/admin/driver_count_prediction`)
      .toPromise();
    const points = generateDates(data.futureDriverCounts);
    setColors(points);
    this.points$ = of(points);
    this.cd.markForCheck();
  }
}

function generateDates(predictedDriverCounts: number[]): DataPoint[] {
  let start = new Date();
  while (!isSunday(start)) {
    start = sub(start, { days: 1 });
  }
  const points: DataPoint[] = [];
  let reachedToday = false;
  for (let i = 0; !reachedToday; i++) {
    const date = add(start, { days: i });
    reachedToday = isToday(date);
    points.push({
      index: i,
      isToday: reachedToday,
      isInThePast: !reachedToday,
      dayOfMonth: getDate(date),
      dayOfWeek: getDay(date),
      previousDayOfWeek: points[points.length - 7],
      count: 0,
      backgroundClass: reachedToday ? 'bg-blue-500' : null,
      textClass: !reachedToday ? 'text-gray-500' : 'text-white',
    });
  }
  let predictionDate = add(new Date(), { days: 1 });
  for (const count of predictedDriverCounts) {
    points.push({
      index: points.length,
      isToday: false,
      isInThePast: false,
      dayOfMonth: getDate(predictionDate),
      dayOfWeek: getDay(predictionDate),
      previousDayOfWeek: points[points.length - 7],
      count,
      backgroundClass: null,
      textClass: 'text-white',
    });
    predictionDate = add(predictionDate, { days: 1 });
  }
  return points;
}

const colors = {
  1: 'bg-gray-500',
  2: 'bg-gray-600',
  3: 'bg-gray-700',
  4: 'bg-gray-800',
  5: 'bg-gray-900',
};

function setColors(points: DataPoint[]) {
  const highest = Math.max(...points.map((point) => point.count));
  const lowest = Math.min(...points.map((point) => point.count));
  const spread = highest - lowest;
  points.forEach((point) => {
    if (point.isToday || point.isInThePast) {
      return;
    }
    const base = point.count - lowest;
    const percent = (base / spread) * 100;
    switch (true) {
      case percent >= 85:
        point.backgroundClass = colors[5];
        return;
      case percent >= 70:
        point.backgroundClass = colors[4];
        return;
      case percent >= 55:
        point.backgroundClass = colors[3];
        return;
      case percent >= 30:
        point.backgroundClass = colors[2];
        return;
      case percent >= 10:
        point.backgroundClass = colors[1];
        return;
      default:
        point.textClass = 'text-black';
    }
  });
}
